import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('bakery_cart');
    return savedCart ? JSON.parse(savedCart) : { items: [], subtotal: 0 };
  });

  // Order Type State (Delivery/Pickup)
  const [orderType, setOrderType] = useState(() => {
    return localStorage.getItem('bakery_order_type') || 'delivery';
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bakery_cart', JSON.stringify(cart));
  }, [cart]);

  // Save orderType to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bakery_order_type', orderType);
  }, [orderType]);

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const addCake = (cakeData) => {
    const {
      cakeId,
      categoryId,
      cakeTitle,
      cakeImage,
      weight,
      flavourId,
      flavourName,
      flavourPrice,
      basePrice,
      baseWeight,
      customDesignFee,
      quantity = 1,
      cakeMessage = ''
    } = cakeData;

    // Calculate cake price: base price for base weight, 75% increase per 500gm above base
    const selectedWeight = parseFloat(weight);
    const baseWeightValue = parseFloat(baseWeight || 1);
    const basePriceValue = parseFloat(basePrice);
    
    let cakePrice = basePriceValue; // Start with base price
    
    // Only apply increment if weight is more than base weight
    if (selectedWeight > baseWeightValue) {
      cakePrice = basePriceValue + (((selectedWeight - baseWeightValue) / 0.5) * (((basePriceValue / baseWeightValue) / 2) * 0.75));
    } else if (selectedWeight < baseWeightValue) {
      cakePrice = basePriceValue * (selectedWeight / baseWeightValue);
    }
    
    const unitPrice = cakePrice + parseFloat(customDesignFee || 0) + 
                     (parseFloat(flavourPrice) * (selectedWeight / 0.5));
    const subtotal = unitPrice * quantity;

    setCart(prevCart => {
      // Find exact match including cake message
      const existingIndex = prevCart.items.findIndex(item =>
        item.type === 'cake' &&
        item.cakeId === cakeId &&
        item.weight === weight &&
        item.flavourId === flavourId &&
        (item.cakeMessage || '') === (cakeMessage || '')
      );

      let newItems;
      if (existingIndex > -1) {
        newItems = [...prevCart.items];
        newItems[existingIndex].quantity += quantity;
        newItems[existingIndex].subtotal = newItems[existingIndex].unitPrice * newItems[existingIndex].quantity;
      } else {
        newItems = [
          ...prevCart.items,
          {
            id: Date.now() + Math.random(),
            type: 'cake',
            cakeId,
            categoryId,
            cakeTitle,
            cakeImage,
            weight,
            flavourId,
            flavourName,
            flavourPrice,
            basePrice,
            baseWeight,
            customDesignFee,
            quantity,
            cakeMessage,
            unitPrice: Math.round(unitPrice),
            subtotal: Math.round(subtotal)
          }
        ];
      }

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId);
      return;
    }

    setCart(prevCart => {
      const newItems = prevCart.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            quantity: newQuantity,
            subtotal: item.unitPrice * newQuantity
          };
        }
        return item;
      });

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const updateWeight = (itemId, newWeight) => {
    setCart(prevCart => {
      const newItems = prevCart.items.map(item => {
        if (item.id === itemId && item.type === 'cake') {
          // Recalculate unit price with new weight
          const selectedWeight = parseFloat(newWeight);
          const baseWeightValue = parseFloat(item.baseWeight || 1);
          const basePriceValue = parseFloat(item.basePrice);
          
          let cakePrice = basePriceValue;
          
          if (selectedWeight > baseWeightValue) {
            cakePrice = basePriceValue + (((selectedWeight - baseWeightValue) / 0.5) * (((basePriceValue / baseWeightValue) / 2) * 0.75));
          } else if (selectedWeight < baseWeightValue) {
            cakePrice = basePriceValue * (selectedWeight / baseWeightValue);
          }
          
          const unitPrice = cakePrice + parseFloat(item.customDesignFee || 0) + 
                           (parseFloat(item.flavourPrice) * (selectedWeight / 0.5));
          const subtotal = unitPrice * item.quantity;
          
          return {
            ...item,
            weight: newWeight,
            unitPrice: Math.round(unitPrice),
            subtotal: Math.round(subtotal)
          };
        }
        return item;
      });

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const removeItem = (itemId) => {
    setCart(prevCart => {
      const newItems = prevCart.items.filter(item => item.id !== itemId);
      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const clearCart = () => {
    setCart({ items: [], subtotal: 0 });
  };

  // Addon Management Functions
  const addAddon = (addonData) => {
    setCart(prevCart => {
      const existingIndex = prevCart.items.findIndex(item =>
        item.type === 'addon' && item.addonId === addonData.id
      );

      let newItems;
      if (existingIndex > -1) {
        // Update quantity if addon already exists
        newItems = [...prevCart.items];
        newItems[existingIndex].quantity += 1;
        newItems[existingIndex].subtotal = newItems[existingIndex].unitPrice * newItems[existingIndex].quantity;
      } else {
        // Add new addon
        newItems = [
          ...prevCart.items,
          {
            id: Date.now() + Math.random(),
            type: 'addon',
            addonId: addonData.id,
            addonName: addonData.name,
            addonImage: addonData.image,
            categoryName: addonData.category_name,
            addonUnitType: addonData.unit_type,
            addonUnit: addonData.unit,
            quantity: 1,
            unitPrice: addonData.price,
            subtotal: addonData.price
          }
        ];
      }

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const removeAddon = (itemId) => {
    setCart(prevCart => {
      const newItems = prevCart.items.filter(item => item.id !== itemId);
      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  // Settings state
  const [settings, setSettings] = useState({
    tax_percentage: 0,
    delivery_charges: 0,
    free_delivery_threshold: 0
  });

  // Fetch settings function
  const fetchSettings = async () => {
    try {
      const response = await fetch('https://panel.supplebakes.com/api/settings/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings({
          tax_percentage: parseFloat(data.settings.tax_percentage) || 0,
          delivery_charges: parseFloat(data.settings.delivery_charges) || 0,
          free_delivery_threshold: parseFloat(data.settings.free_delivery_threshold) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const getTotals = (deliveryType = 'delivery') => {
    const subtotal = cart.subtotal;
    
    // Calculate Tax
    const tax = subtotal * (settings.tax_percentage / 100);

    // Calculate Delivery Fee
    let deliveryFee = settings.delivery_charges;
    
    // If delivery type is pickup, or subtotal meets free delivery threshold, fee is 0
    if (deliveryType === 'pickup' || (settings.free_delivery_threshold > 0 && subtotal >= settings.free_delivery_threshold)) {
      deliveryFee = 0;
    }

    const total = subtotal + tax + deliveryFee;

    return {
      subtotal: Math.round(subtotal),
      tax: Math.round(tax * 100) / 100,
      deliveryFee: Math.round(deliveryFee),
      total: Math.round(total * 100) / 100,
      settings // Expose settings for UI use (e.g. "Free delivery above X")
    };
  };

  const getItemCount = () => {
    return cart.items.length;
  };

  const value = {
    cart,
    addCake,
    updateQuantity,
    updateWeight,
    removeItem,
    clearCart,
    getTotals,
    getItemCount,
    settings,
    fetchSettings,
    orderType,
    setOrderType,
    addAddon,
    removeAddon
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
